import {
  toIdArray,
  type IdValue
} from '../../../../../utils/companyCategorySubCategory';
import {
  getPeerCompanyMetricValue,
  getPeerIndustryAverage,
  MISSING_PEER_VALUE
} from '../../gapAnalysis/gapAnalysisPeerData';

import {StrategyMetrics} from './ESGMetrics';
import {
  get75PeerBenchmarkData,
  get6PeerBenchmarkData,
  getDefaultPeerBenchmarkData,
  type PeerBenchmarkMetricView,
  type PeerBenchmarkPillarRow,
  get2PeerBenchmarkData,
  get9PeerBenchmarkData,
  get83PeerBenchmarkData,
  get83EmptyPeerBenchmarkData,
  get3PeerBenchmarkData,
  createStrategyPeerBenchmarkData
} from './peerBenchmarkingDefaultData';

/**
 * Keys: `subCategory::year` or `subCategory::*`.
 * `year` may be `2023` (calendar / single picker) or `2023-2024` (FY range). Lookup tries the
 * incoming value first, then the alternate form so one override row can match either shape.
 */
const PEER_BENCHMARK_OVERRIDES: Record<string, PeerBenchmarkPillarRow[]> = {
  // Sample: fictional subCategory/year so production routing is unchanged — swap for real IDs + replace array body
  '6::2023-2024': get6PeerBenchmarkData(),
  '6::2026-2027': get6PeerBenchmarkData(),
  '75::2026-2027': get75PeerBenchmarkData(),
  '75::2023-2024': get75PeerBenchmarkData(),
  '2::2026-2027': get2PeerBenchmarkData(),
  '2::2023-2024': get2PeerBenchmarkData(),
  '9::2026-2027': get9PeerBenchmarkData(),
  '9::2023-2024': get9PeerBenchmarkData(),
  '83::2025-2026': get83PeerBenchmarkData(),
  '83::2026-2027': get83PeerBenchmarkData(),
  '83::*': get83EmptyPeerBenchmarkData(),
  '3::2026-2027': get3PeerBenchmarkData(),
  '3::2023-2024': get3PeerBenchmarkData()
};

function withStrategyPillar(
  pillars: PeerBenchmarkPillarRow[],
  year: string
): PeerBenchmarkPillarRow[] {
  const peerCompanies = pillars[0]?.companyData ?? [];
  const base = createStrategyPeerBenchmarkData(peerCompanies);
  const metricData: Record<string, PeerBenchmarkMetricView> = {};

  for (const metric of StrategyMetrics) {
    const averageDetail = getPeerIndustryAverage(year, metric.label);
    metricData[metric.value] = {
      average: base.average,
      averageDetail:
        averageDetail === MISSING_PEER_VALUE
          ? base.averageDetail
          : averageDetail,
      companyData: peerCompanies.map((company) => {
        const detail = getPeerCompanyMetricValue(
          year,
          company.label,
          metric.label
        );

        return {
          name: company.name,
          label: company.label,
          value: '',
          detail:
            detail === MISSING_PEER_VALUE
              ? 'Strategy details are not available for this company.'
              : detail
        };
      })
    };
  }

  const strategyPillar: PeerBenchmarkPillarRow = {...base, metricData};

  if (pillars.some((pillar) => pillar.pillar === 'Strategy')) {
    return pillars.map((pillar) =>
      pillar.pillar === 'Strategy' ? strategyPillar : pillar
    );
  }

  return [...pillars, strategyPillar];
}

export function resolvePeerBenchmarkMetricView(
  pillar: PeerBenchmarkPillarRow,
  metricValue: string
): PeerBenchmarkMetricView {
  const override = pillar.metricData?.[metricValue];
  if (override) {
    return {
      average: override.average ?? pillar.average,
      averageDetail: override.averageDetail ?? pillar.averageDetail,
      companyData: override.companyData ?? pillar.companyData
    };
  }

  return {
    average: pillar.average,
    averageDetail: pillar.averageDetail,
    companyData: pillar.companyData
  };
}

function overrideKey(subCategory: number, year: string): string {
  return `${subCategory}::${year}`;
}

/** Same FY in both notations used by `yearSelect` (single vs Apr–Mar range). */
function peerYearLookupKeys(year: string): string[] {
  const t = year.trim();
  if (!t) {
    return [];
  }
  const keys: string[] = [t];
  const rangeMatch = /^(\d{4})-(\d{4})$/.exec(t);
  if (rangeMatch) {
    const start = rangeMatch[1];
    if (start && !keys.includes(start)) {
      keys.push(start);
    }

    return keys;
  }
  if (/^\d{4}$/.test(t)) {
    const y = Number(t);
    const rangeKey = `${y}-${y + 1}`;
    if (!keys.includes(rangeKey)) {
      keys.push(rangeKey);
    }
  }

  return keys;
}

function resolvePeerBenchmarkForId(
  subCategory: number,
  year: string
): PeerBenchmarkPillarRow[] | undefined {
  const normalizedYear = year?.trim() ?? '';

  if (normalizedYear) {
    for (const yKey of peerYearLookupKeys(normalizedYear)) {
      const exact = PEER_BENCHMARK_OVERRIDES[overrideKey(subCategory, yKey)];
      if (exact?.length) {
        return exact;
      }
    }
  }

  const subCategoryWildcard =
    PEER_BENCHMARK_OVERRIDES[overrideKey(subCategory, '*')];
  if (subCategoryWildcard?.length) {
    return subCategoryWildcard;
  }

  return undefined;
}

function withEnvironmentalDefaults(
  pillars: PeerBenchmarkPillarRow[]
): PeerBenchmarkPillarRow[] {
  return pillars.map((pillar) => {
    if (pillar.pillar !== 'Environmental') {
      return pillar;
    }

    const formatVal = (val: string | number | React.ReactNode | unknown, factor: number, suffix: string = '') => {
      if (val === 'NA' || val === 'Not Disclosed' || !val) return val;
      if (typeof val !== 'string' && typeof val !== 'number') return val; // Handle ReactNode gracefully
      
      let num = NaN;
      if (typeof val === 'number') num = val;
      if (typeof val === 'string') {
        const parsed = parseFloat(val.replace(/[^\d.-]/g, ''));
        if (!isNaN(parsed)) num = parsed;
      }
      
      if (!isNaN(num)) return +(num * factor).toFixed(2) + suffix;

      return val;
    };

    const metricData = {...(pillar.metricData ?? {})};

    pillar.metrics.forEach((metricOption) => {
      const key = metricOption.value;
      if (!metricData[key]) {
        // Create a deterministic factor based on the metric value (e.g. 1 -> 0.85, 2 -> 1.15, etc.)
        const parsedKey = parseInt(key) || 0;
        const factor = 0.6 + (parsedKey % 7) * 0.15; 

        // Assign a rough unit suffix based on label
        let suffix = ' tCO2e';
        const label = metricOption.label.toLowerCase();
        if (label.includes('intensity') && label.includes('physical')) suffix = ' tCO2e / Sq.Ft';
        else if (label.includes('intensity') && label.includes('economic')) suffix = ' tCO2e / ₹Cr';
        else if (label.includes('energy')) suffix = ' kWh';
        else if (label.includes('water')) suffix = ' KL';
        else if (label.includes('waste')) suffix = ' tons';
        else if (label.includes('revenue')) suffix = ' ₹Cr';
        else if (label.includes('rate') || label.includes('management')) suffix = '%';

        metricData[key] = {
          average: formatVal(pillar.average, factor, suffix) as any,
          averageDetail: `Static analysis for ${metricOption.label}.`,
          companyData: pillar.companyData.map(c => ({
            ...c, 
            value: formatVal(c.value, factor, suffix) as any
          }))
        };
      }
    });

    return {...pillar, metricData};
  });
}

export function getPeerBenchmarkDataArray(
  subCategory: IdValue,
  year: string
): PeerBenchmarkPillarRow[] {
  for (const id of toIdArray(subCategory)) {
    const resolved = resolvePeerBenchmarkForId(id, year);
    if (resolved) {
      return withEnvironmentalDefaults(withStrategyPillar(resolved, year));
    }
  }

  return withEnvironmentalDefaults(
    withStrategyPillar(getDefaultPeerBenchmarkData(), year)
  );
}
